import React from "react";
import { Icon } from "@iconify/react";

export const AvatarAiCaptureInstruction = ({
  label = "Guarda dentro l'obiettivo della camera",
  className = "",
}) => (
  <div
    className={`absolute left-1/2 top-2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/70 px-3 py-1 text-[11px] font-semibold text-white shadow-lg pointer-events-none ${className}`}
  >
    <Icon icon="mdi:eye-outline" className="h-3.5 w-3.5" />
    {label}
  </div>
);

export const AvatarAiCountdownOverlay = ({
  countdown,
  label = "Guarda nell'obiettivo",
  className = "",
}) => {
  if (countdown === null || countdown <= 0) return null;

  return (
    <div
      className={`absolute inset-0 flex flex-col items-center justify-center bg-black/35 text-white pointer-events-none ${className}`}
    >
      <span
        key={countdown}
        className="font-black"
        style={{
          fontSize: "8rem",
          lineHeight: 1,
          textShadow: "0 6px 20px rgba(0,0,0,0.55)",
          animation: "avatarAiCountdown 0.9s ease-out forwards",
        }}
      >
        {countdown}
      </span>
      <span className="mt-3 rounded-full bg-white/15 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.12em] backdrop-blur">
        {label}
      </span>
      <style>{`
        @keyframes avatarAiCountdown {
          0% { transform: scale(0.4); opacity: 0; }
          25% { transform: scale(1.25); opacity: 1; }
          100% { transform: scale(0.95); opacity: 0.85; }
        }
      `}</style>
    </div>
  );
};

export const AvatarAiCaptureUploadBadge = ({
  progress = 0,
  label = "Preparo la foto...",
}) => (
  <div className="absolute inset-0 flex items-end justify-center bg-black/25 pb-4 pointer-events-none">
    <div className="flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow">
      <Icon icon="mdi:loading" className="h-4 w-4 animate-spin" />
      {progress > 0 && progress < 100 ? `Carico foto ${progress}%` : label}
    </div>
  </div>
);

export const AvatarAiPlayfulLoader = ({
  phase,
  quip,
  sourceSrc,
  progressPct = 0,
  themeLabel = "",
}) => {
  const isSubmit = phase === "submit";
  return (
    <div className="mt-6 rounded-2xl bg-gradient-to-br from-fuchsia-50 via-white to-cyan-50 p-6">
      <div className="flex flex-col items-center gap-4">
        <div className="relative h-32 w-32">
          <div
            className="absolute inset-0 rounded-full bg-gradient-to-tr from-fuchsia-500 via-pink-400 to-amber-400 animate-spin"
            style={{ animationDuration: "3.5s" }}
          />
          <div className="absolute inset-1 rounded-full bg-white" />
          <div className="absolute inset-2 overflow-hidden rounded-full">
            {sourceSrc ? (
              <img
                src={sourceSrc}
                alt="source"
                className="h-full w-full object-cover"
                style={{ filter: "saturate(1.2)" }}
              />
            ) : (
              <div className="h-full w-full bg-slate-100" />
            )}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(115deg, transparent 0%, transparent 35%, rgba(255,255,255,0.55) 50%, transparent 65%, transparent 100%)",
                backgroundSize: "200% 100%",
                animation: "avatarAiShimmer 1.8s linear infinite",
              }}
            />
          </div>
          <Icon
            icon="mdi:auto-fix"
            className="absolute -top-1 -right-1 h-7 w-7 text-fuchsia-600 animate-pulse"
          />
          <Icon
            icon="mdi:star-four-points"
            className="absolute -bottom-1 -left-1 h-5 w-5 text-amber-400 animate-bounce"
          />
        </div>

        <p className="text-sm font-semibold text-slate-700 min-h-[1.25rem] text-center transition-opacity">
          {quip}
        </p>

        {!isSubmit && (
          <div className="w-full max-w-xs">
            <div className="h-2 overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 transition-all duration-500"
                style={{ width: `${Math.max(4, progressPct)}%` }}
              />
            </div>
            <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
              <span>{themeLabel}</span>
              <span>{progressPct}%</span>
            </div>
          </div>
        )}

        <p className="text-[11px] text-slate-400 text-center max-w-xs">
          Tempo medio 30-60 secondi. Non chiudere la finestra.
        </p>
      </div>
      <style>{`
        @keyframes avatarAiShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

export const AvatarAiThemeSkeleton = () => (
  <div className="rounded-lg border border-slate-200 overflow-hidden">
    <div
      className="aspect-square"
      style={{
        background:
          "linear-gradient(110deg, #f1f5f9 8%, #e2e8f0 18%, #f1f5f9 33%)",
        backgroundSize: "200% 100%",
        animation: "avatarAiSkeleton 1.6s linear infinite",
      }}
    />
    <div className="px-3 py-2 bg-white space-y-1.5">
      <div className="h-3 w-3/4 rounded bg-slate-200 animate-pulse" />
      <div className="h-2.5 w-1/2 rounded bg-slate-100 animate-pulse" />
    </div>
    <style>{`
      @keyframes avatarAiSkeleton {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
  </div>
);

export const AvatarAiJobTile = ({
  label,
  type,
  job,
  selected,
  onSelect,
  aspectRatio = "1 / 1",
  imageFitClass = "object-cover",
}) => {
  const status = job?.status;
  const completed = status === "COMPLETED";
  const failed = status === "FAILED";
  const ringClass = selected
    ? "ring-2 ring-fuchsia-600 border-fuchsia-600"
    : completed
      ? "border-slate-200 hover:border-fuchsia-400 cursor-pointer"
      : "border-slate-200";
  return (
    <div
      onClick={onSelect}
      className={`rounded-xl border bg-white overflow-hidden ${ringClass}`}
    >
      <div
        className="relative w-full bg-slate-100"
        style={{ aspectRatio }}
      >
        {completed && job.resultUrl ? (
          <img
            src={job.resultUrl}
            alt={label}
            className={`absolute inset-0 w-full h-full ${imageFitClass}`}
          />
        ) : failed ? (
          <div className="flex flex-col items-center justify-center h-full p-3 text-center">
            <Icon
              icon="mdi:alert-circle-outline"
              className="h-8 w-8 text-red-500"
            />
            <p className="mt-1 text-[11px] text-red-600 line-clamp-3">
              {job?.errorMessage || "Errore"}
            </p>
          </div>
        ) : (
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(110deg, #f1f5f9 8%, #e2e8f0 18%, #f1f5f9 33%)",
              backgroundSize: "200% 100%",
              animation: "avatarAiSkeleton 1.6s linear infinite",
            }}
          />
        )}
        {selected && (
          <div className="absolute top-1 right-1 rounded-full bg-fuchsia-600 text-white p-1">
            <Icon icon="mdi:check" className="h-4 w-4" />
          </div>
        )}
      </div>
      <div className="px-3 py-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-800 truncate">
          {label}
        </span>
        <span
          className={`text-[10px] uppercase font-bold ${
            type === "pro" ? "text-emerald-700" : "text-fuchsia-700"
          }`}
        >
          {type === "pro" ? "Pro" : "Fun"}
        </span>
      </div>
      <style>{`
        @keyframes avatarAiSkeleton {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};
